'use client';

import { SessionRefresher } from '@/features/auth/components/SessionRefresher';
import { ViewportManager } from '@/shared/components/ViewportManager';
import { Footer } from '@/shared/components/organisms/Footer';
import IndicatorContainer from '@/shared/components/organisms/IndicatorContainer';
import { MessagePopupContainer } from '@/shared/components/organisms/MessagePopupContainer';
import { Navigation } from '@/shared/components/organisms/Navigation';
import { useDeviceDetection } from '@/shared/hooks/useDeviceDetection';
import { initMixpanel } from '@/shared/utils/mixpanel';
import '@/styles/globals.css';
import { SessionProvider } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Toaster } from 'sonner';

export function LayoutProvider({ children }: { children: React.ReactNode }) {
  const [isPopup, setIsPopup] = useState(false);
  const pathname = usePathname();
  const { isMobile, isLoading } = useDeviceDetection();

  useEffect(() => {
    setIsPopup(
      typeof window !== 'undefined' && !!window.opener && window.name !== '',
    );
  }, []);

  useEffect(() => {
    initMixpanel();
  }, []);

  if (isPopup) {
    return (
      <>
        <ViewportManager />
        {children}
      </>
    );
  }

  if (pathname === '/onboarding') {
    return (
      <>
        <ViewportManager />
        <SessionProvider>
          <SessionRefresher />
          {children}
        </SessionProvider>
      </>
    );
  }

  // 모바일 로그인 화면에서는 Navigation과 Footer 제외
  const isAuthPage = pathname === '/signin';
  const shouldHideNavigation = (isLoading || isMobile) && isAuthPage;

  if (shouldHideNavigation) {
    return (
      <>
        <ViewportManager />
        <Toaster position="top-right" richColors />
        <SessionProvider>
          <SessionRefresher />
          {children}
          <MessagePopupContainer />
          <IndicatorContainer />
        </SessionProvider>
      </>
    );
  }

  return (
    <>
      <ViewportManager />
      <Toaster position="top-right" richColors />
      <SessionProvider>
        <SessionRefresher />
        <div className="bg-background border-b border-border sticky top-0 z-50">
          <Navigation className="container" />
        </div>
        <div className="bg-(--color-extra-main-background)">
          <main className="flex-1">
            {children}
            <MessagePopupContainer />
            <IndicatorContainer />
          </main>
        </div>
        <div className="border-t border-border">
          <Footer className="container pt-12 pb-8" />
        </div>
      </SessionProvider>
    </>
  );
}
